import { type ReactNode } from 'react'
import { Box, Paper } from '@mui/material'

import { AttachmentList } from '@/apps/latest/features/attachments/AttachmentList'
import { TabBar } from '@/components/molecules/TabBar'
import { RADIUS } from '@/shared/styles/radii'
import type { ConversationSegment } from '@/shared/types/yakurekiCore'

import type { NutritionReport } from './api'
import { NutritionConversation } from './NutritionConversation'
import { NutritionIntakeSection } from './NutritionIntakeSection'
import { NutritionReportPanel } from './NutritionReportPanel'

interface Props {
  // 表に出す回 (記録)。今回でも前の回でも同じ形
  eventId: string
  report: NutritionReport
  segments: ConversationSegment[]
  // 右の列の上の段 (その回のメモ)
  memo?: ReactNode
  // タブの上の行 (回を選ぶ)。持ち主は呼び出し側
  header?: ReactNode
  // 開いているタブ。持ち主は呼び出し側 (回を替えても同じタブのまま、その回の中身に替わる)
  tab: RoundTab
  onTabChange: (tab: RoundTab) => void
}

export type RoundTab = 'report' | 'intake' | 'materials' | 'conversation'

const TABS: { key: RoundTab; label: string }[] = [
  { key: 'report', label: '報告書' },
  { key: 'intake', label: '栄養価' },
  { key: 'materials', label: '資料' },
  { key: 'conversation', label: '会話' },
]

// 1 回の記録を、見る目的ごとの 4 つのタブで出す (design-book ADR-0082・ADR-0085)。
//   報告書 … 様式の格子と右の列 (メモ・要約)。出す物 (Excel の 1 枚目)
//   栄養価 … 食事の欄から計算した表 (Excel の後ろの 4 シート)
//   資料   … この回の資料 (問診票・体組成計の結果・検査の結果など。生成の材料)
//   会話   … この回の会話の全文 (読むだけ)
// どの回 (今回・前の回) も同じ 4 つで、同じ形で直せる (ADR-0086 決定 6)。回を選ぶのはタブの上の行 (時間軸はタブより 1 つ上の層)。
// 器は薬剤師の記録ブロックと同じ Paper と TabBar (ADR-0059)。回を替えても開いているタブは保つ (前回 → 前々回の資料を
// 同じタブのまま送れる)。
export const NutritionRoundTabs = ({
  eventId,
  report,
  segments,
  memo,
  header,
  tab,
  onTabChange,
}: Props) => {
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: `${RADIUS.xl}px`, overflow: 'hidden' }}
    >
      {header}
      <TabBar<RoundTab> tabs={TABS} active={tab} onChange={onTabChange} />
      <Box sx={{ p: 2 }}>
        {tab === 'report' && (
          <NutritionReportPanel
            eventId={eventId}
            fields={report.fields}
            stamp={report.stamp}
            memo={memo}
          />
        )}
        {tab === 'intake' && (
          <NutritionIntakeSection eventId={eventId} intake={report.intake} />
        )}
        {tab === 'materials' && (
          <AttachmentList
            yakurekiId={eventId}
            skippedIds={report.skipped_attachment_ids ?? []}
            readIds={report.attachment_ids}
            documentName="報告書"
          />
        )}
        {tab === 'conversation' && (
          <NutritionConversation segments={segments} />
        )}
      </Box>
    </Paper>
  )
}
